import { calculateAgeForTest, getNormalValues, parseDateOnly } from "@utils/CommonUtil";
import {
  calculateMeanDeviation,
  calculatePatternStandardDeviation,
  calculatePdMapValuesAndGh,
  getTestConfig,
  removeMultiple,
} from "../util/TestEntryUtil";

import StatisticsMap from "./StatisticsMap";
import { calculateGht } from "../ght/Ght";
import { calculateVfi } from "../util/VisualFieldIndex";
import { cloneDeep } from "lodash";
import { VFABehaviour } from "types/tests";
import {
  calculateBebieCurveFromTotalDeviation,
  calculateMeanSensitivity,
  calculateReliabilityFactor,
  flattenComputedMetrics,
  getDisplayedThresholds,
  getFnFpError,
} from "@utils";

const BACKEND_ADJUSTED_BEHAVIOURS = new Set(
  [
    VFABehaviour.FAST_4_2,
    VFABehaviour.FAST_4,
    VFABehaviour.FULL_THRESH_4,
    VFABehaviour.SITA_LIKE,
  ].map(String),
);

export function calculateMapValues(test, finalThresholds, getGHT = false) {
  const { eye, testType } = test;
  let percentagesStr = "percentages";
  const theDate = new Date("2024-06-28");
  if (new Date(test.startDate) < theDate) {
    percentagesStr = "percentages_old";
  }
  const patientAge = calculateAgeForTest(new Date(test.startDate), parseDateOnly(test.patient.dob));
  const normalValues = getNormalValues({
    age: patientAge,
    eye: eye,
    testType: testType,
    testBehaviour: test.testBehaviour,
  });

  const { middleCol, middleRow, emptyCellsIndex, pointsToIgnoreForPdMap, angles } = getTestConfig(
    test.testType,
    test.eye,
  );

  const totalDeviationThresholds = finalThresholds.map((v, i) => v - normalValues[i]);
  const meanDeviationValue = calculateMeanDeviation(
    removeMultiple(cloneDeep(finalThresholds), emptyCellsIndex),
    removeMultiple(cloneDeep(normalValues), emptyCellsIndex),
  );
  const { pdMapValues, gh } = calculatePdMapValuesAndGh(
    cloneDeep(finalThresholds),
    cloneDeep(normalValues),
    pointsToIgnoreForPdMap,
  );

  const psd = calculatePatternStandardDeviation(
    removeMultiple(cloneDeep(pdMapValues), emptyCellsIndex),
  );

  const ntdMapElems = StatisticsMap(
    totalDeviationThresholds,
    eye,
    testType,
    middleCol,
    middleRow,
    percentagesStr,
  );
  const psdMapElems = StatisticsMap(
    pdMapValues,
    eye,
    testType,
    middleCol,
    middleRow,
    percentagesStr,
  );

  const vfiCalculated = calculateVfi(
    testType,
    eye,
    cloneDeep(finalThresholds),
    cloneDeep(pdMapValues),
    cloneDeep(normalValues),
    meanDeviationValue,
    percentagesStr,
  );

  let ght = null;
  if ((testType === "24-2" || testType === "30-2") && getGHT) {
    ght = calculateGht(
      testType,
      eye,
      psdMapElems.ps,
      gh,
      meanDeviationValue,
      getFnFpError(test, "fp"),
      vfiCalculated,
      percentagesStr,
    );
  }
  return {
    md: meanDeviationValue,
    ntd: totalDeviationThresholds,
    psd: psd,
    npd: pdMapValues,
    ntdGs: ntdMapElems.images,
    npdGs: psdMapElems.images,
    ght: ght,
    vfi: vfiCalculated,
    ntdPs: ntdMapElems.ps,
    npdPs: psdMapElems.ps,
    angles: angles,
  };
}

export function resolveVfaMapValues(test) {
  const flattened = flattenComputedMetrics(test?.computedMetrics);
  if (flattened) return flattened;

  // Raw points are no longer adjusted in the browser. A local fallback for
  // these strategies would silently publish results on the wrong dB scale.
  if (BACKEND_ADJUSTED_BEHAVIOURS.has(String(test?.testBehaviour))) {
    console.warn(`Adjusted VFA metrics unavailable for test ${test?.uuid}`);
    return null;
  }

  try {
    const maps = calculateMapValues(test, test.points, true);
    const { emptyCellsIndex } = getTestConfig(test.testType, test.eye);
    const thresholds = getDisplayedThresholds(test.points, test.testBehaviour);

    return {
      ...maps,
      thresholds,
      meanSensitivity: calculateMeanSensitivity(thresholds, emptyCellsIndex),
      reliabilityFactor: calculateReliabilityFactor(test.fpFnInfo),
      bebie: calculateBebieCurveFromTotalDeviation(maps.ntd, emptyCellsIndex),
    };
  } catch (error) {
    console.warn(`VFA metrics unavailable for test ${test?.uuid}`, error);
    return null;
  }
}

export function getItemId(row, col, middleCol, middleRow) {
  let c = "";
  if (row === middleRow) {
    c += "bottom-border";
  }
  if (col === middleCol) {
    if (c !== "") c += "-";
    c += "right-border";
  }
  return c;
}
